// src/pages/client/BookingPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, addMonths, subMonths } from 'date-fns';
import { scheduleService } from '../../services/scheduleService';
import { appointmentService } from '../../services/appointmentService';
import paymentService from '../../services/paymentService';
import loyaltyService, { LoyaltyData, RedeemResponse } from '../../services/loyaltyService';
import promotionService, { StylistPromotion } from '../../services/promotionService';
import type { PromotionApplied, DiscountSource } from '../../types/appointment';
import { useAppSelector } from '../../hooks/useRedux';
import { formatCurrency } from '../../utils/formatters';
import { resolvedBookingDownPaymentPercent } from '../../utils/downPaymentDisplay';
import {
  formatLocalDateKey,
  getTimeSlotDisabledState,
  isTimeSlotSelectable,
  normalizeSlotsFromApi,
} from '../../utils/bookingTimeSlots';
import ServiceSummary from '../../components/client/ServiceSummary';
import LoyaltyPointsSelector from '../../components/client/LoyaltyPointsSelector';
import PaymentForm from '../../components/client/PaymentForm';
import ChatInterface from '../../components/common/ChatInterface';
import AddOnsSelection from '../../components/AddOnsSelection';
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  CalendarIcon,
  AlertIcon,
  CheckIcon,
  ChatIcon
} from '../../components/common/Icons';
import '../../styles/BookingPage.css';
import {
  clearPendingBooking,
  peekPendingBooking,
  savePendingBooking,
  useIsSignedIn,
  useRequireSignIn,
} from '../../utils/guestAccess';

interface BookingState {
  stylistId: string;
  serviceId: string;
  service: { name: string; price: number; duration: number; description?: string; category?: string };
  stylist: { name: string; profilePhoto?: string };
  isMobileService?: boolean;
  location?: any;
}

interface SelectedAddOn {
  sourceAddOnId?: string;
  sourceType: 'predefined' | 'custom';
  name: string;
  description?: string;
  priceCents: number;
  durationMinutes: number;
}

// Define the interface for BookingSuccessState to match with the success page
interface BookingSuccessState {
  appointmentId: string;
  serviceDetails: {
    name: string;
    price: number;
    duration: number;
    description?: string;
  };
  stylistDetails: {
    name: string;
    profilePhoto?: string;
  };
  appointmentDateTime: string;
  appointment?: any;
  isDownPayment?: boolean;
  downPaymentAmount?: number;
  downPaymentPercentage?: number;
  remainingAmount?: number;
  remainingPercentage?: number;
  totalAmount?: number;
}

type TimeSlot = { 
  startTime: string; 
  endTime?: string; 
  displayTime?: string; // 24-hour format in client's timezone
  displayTime12?: string; // 12-hour format in client's timezone
  available?: boolean; 
};

const Step: React.FC<{ number: number; label: string; active: boolean; completed: boolean }> = ({ number, label, active, completed }) => (
  <div className="booking-flow__step">
    <div className={`booking-flow__step-circle ${active ? 'booking-flow__step-circle--active' : ''} ${completed ? 'booking-flow__step-circle--completed' : ''}`}> 
      {completed ? <CheckIcon size={16} /> : number}
    </div>
    <div className={`booking-flow__step-label ${active ? 'booking-flow__step-label--active' : ''} ${completed ? 'booking-flow__step-label--completed' : ''}`}>{label}</div>
    {number < 3 && (
      <div className="booking-flow__step-connector">
        <div className="booking-flow__step-connector-progress" style={{ width: completed ? '100%' : '0%' }} />
      </div>
    )}
  </div>
);

const CalendarDay: React.FC<{ day: number; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean; isAvailable: boolean; onClick: () => void }> = ({ day, isCurrentMonth, isSelected, isToday, isAvailable, onClick }) => (
  <div className={`calendar-day ${!isCurrentMonth ? 'calendar-day--faded' : ''} ${isSelected ? 'calendar-day--selected' : ''} ${isToday ? 'calendar-day--today' : ''} ${isAvailable ? 'calendar-day--available' : 'calendar-day--unavailable'}`} onClick={isAvailable && isCurrentMonth ? onClick : undefined}>
    {day}
    {isToday && !isSelected && <div className="calendar-day__today-indicator" />}
  </div>
);

const BookingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector(s => s.auth);
  // Guests reach this page from /browse/:shortCode/book (App Store 5.1.1(v)) and
  // may choose a date and time; Review & Book and everything after need an account.
  const isSignedIn = useIsSignedIn();
  const isGuest = !isSignedIn;
  const requireSignIn = useRequireSignIn();
  // After sign-in, /client/booking is opened without router state: resume the
  // guest's saved selection (read-only here; cleared in an effect below).
  const [restored] = useState(() =>
    location.state || !isSignedIn ? null : peekPendingBooking()
  );
  const bookingData = (location.state || restored?.bookingData) as BookingState;
  const pendingRestoreRef = useRef(restored);

  // Stepper
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Select Date', 'Select Time', 'Review & Book'];

  // Selection
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  /** yyyy-MM-dd of the day whose slots are currently loaded (null until a load finishes). */
  const [slotsLoadedFor, setSlotsLoadedFor] = useState<string | null>(null);
  /** Shown when a slot saved before sign-in was taken meanwhile (slots stay visible). */
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  /** Bumps on a timer while choosing a time so "past" slots grey out if the user leaves the tab open. */
  const [timeStepClockTick, setTimeStepClockTick] = useState(0);

  // UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loyaltyMsg, setLoyaltyMsg] = useState<string | null>(null);

  // Loyalty & Pricing
  const [loyaltyData, setLoyaltyData] = useState<LoyaltyData | null>(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loyaltyRedemptionId, setLoyaltyRedemptionId] = useState<string | null>(null);
  const [finalPrice, setFinalPrice] = useState(bookingData?.service?.price || 0);
  /** Stable across network retries of the same redeem tap */
  const redeemIdempotencyKeyRef = useRef<string | null>(null);

  // Promotion state
  const [availablePromos, setAvailablePromos] = useState<StylistPromotion[]>([]);
  const [appliedPromo, setAppliedPromo] = useState<StylistPromotion | null>(null);
  const [promoDiscount, setPromoDiscount] = useState(0);

  // Payment
  const [setupIntentData, setSetupIntentData] = useState<{ clientSecret: string; setupIntentId: string; appointmentId: string } | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [, setCreatedAppointmentId] = useState<string | null>(null);

  // Down Payment Policy
  const [downPaymentPolicy, setDownPaymentPolicy] = useState<{
    enabled: boolean;
    percentage: number;
  } | null>(null);

  // Chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatThreadId, setChatThreadId] = useState<string | null>(null);

  // Add-ons
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOn[]>([]);

  // /client/booking is already behind ProtectedRoute; the guest route must not
  // bounce to /login before the guest has even picked a time.
  useEffect(() => {
    if (!restored) return;
    clearPendingBooking();
    const [year, month, day] = restored.selectedDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    setCurrentMonth(date);
    setSelectedDate(date);
    setActiveStep(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Review & Book (loyalty, add-ons, card, appointment) is account-only — e.g. if
  // the session ends mid-flow, step back to time selection.
  useEffect(() => {
    if (isGuest && activeStep > steps.length - 2) setActiveStep(steps.length - 2);
  }, [isGuest, activeStep, steps.length]);

  // Re-select the restored slot only if live availability still offers it.
  useEffect(() => {
    const pending = pendingRestoreRef.current;
    if (!pending || slotsLoadedFor !== pending.selectedDate) return;
    pendingRestoreRef.current = null;
    const match = availableSlots.find(
      (slot) =>
        new Date(slot.startTime).getTime() === new Date(pending.slotStartTime).getTime() &&
        isTimeSlotSelectable(slot, selectedDate)
    );
    if (match) {
      setSelectedTimeSlot(match);
    } else {
      setRestoreNotice('That time is no longer available. Please choose another time.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSlots, slotsLoadedFor]);

  useEffect(() => {
    if (!bookingData?.stylistId || !bookingData?.serviceId) {
      setError('Missing booking information');
      return;
    }
    setFinalPrice(bookingData.service.price);
    if (isSignedIn) fetchLoyalty();
    fetchDownPaymentPolicy();
    fetchPromotions();
    initChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingData]);

  // Recalculate final price when add-ons, loyalty discount, or promo discount change
  useEffect(() => {
    if (!bookingData?.service) return;
    const servicePrice = bookingData.service.price ?? 0;
    const approvedAddOns = selectedAddOns.filter(addon => addon.sourceType === 'predefined');
    const addOnsTotal = approvedAddOns.reduce((sum, addon) => sum + addon.priceCents, 0) / 100;
    const totalDiscount = loyaltyDiscount + promoDiscount;
    // Discounts apply to service price only, not add-ons (matching mobile behaviour)
    const priceAfterDiscount = Math.max(0, servicePrice - totalDiscount);
    setFinalPrice(priceAfterDiscount + addOnsTotal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddOns, loyaltyDiscount, promoDiscount, bookingData?.service?.price]);

  const initChat = () => {
    if (bookingData.stylistId && user?.id) {
      setChatThreadId(`booking-${user.id}-${bookingData.stylistId}-${Date.now()}`);
    }
  };

  const fetchLoyalty = async () => {
    try {
      const data = await loyaltyService.getLoyalty();
      setLoyaltyData(data);
    } catch {
      // ignore
    }
  };

  const fetchPromotions = async () => {
    if (!bookingData?.stylistId) return;
    try {
      const promos = await promotionService.getStylistPromotions(
        bookingData.stylistId,
        bookingData.service.category
      );
      setAvailablePromos(promos.filter(p => p.isActive));
    } catch {
      // non-critical, ignore
    }
  };

  const calculatePromoDiscount = (promo: StylistPromotion, servicePrice: number): number => {
    let amount = 0;
    if (promo.discountType === 'percentage') {
      amount = servicePrice * (promo.discountValue / 100);
    } else {
      amount = promo.discountValue;
    }
    if (promo.maxDiscountAmount) {
      amount = Math.min(amount, promo.maxDiscountAmount);
    }
    return Math.min(amount, servicePrice); // cannot exceed service price
  };

  const handleApplyPromo = (promo: StylistPromotion) => {
    if (loyaltyDiscount > 0) {
      setError('You have already redeemed loyalty points. Remove the loyalty discount first to apply a promotion.');
      return;
    }
    if (appliedPromo?._id === promo._id) {
      // toggle off
      setAppliedPromo(null);
      setPromoDiscount(0);
      return;
    }
    // C5: Enforce minimum booking amount
    const minBookingRequired = promo.minBookingAmount ?? 0;
    if (minBookingRequired > 0 && bookingData.service.price < minBookingRequired) {
      setError(`This promotion requires a minimum booking of ${formatCurrency(minBookingRequired)}.`);
      return;
    }
    const discountAmt = calculatePromoDiscount(promo, bookingData.service.price);
    setAppliedPromo(promo);
    setPromoDiscount(discountAmt);
    setError(null);
  };

  const fetchDownPaymentPolicy = async () => {
    try {
      const response = await paymentService.getStylistDownPaymentPolicy(bookingData.stylistId);
      const policy = response.enabled !== undefined ? response : { enabled: false, percentage: 0 };
      setDownPaymentPolicy(policy);
    } catch {
      setDownPaymentPolicy({ enabled: false, percentage: 0 });
    }
  };

  const stylistPoints = useMemo(
    () => loyaltyData?.breakdown?.find(b => b.stylist?._id === bookingData.stylistId)?.availablePoints || 0,
    [loyaltyData, bookingData.stylistId]
  );

  useEffect(() => { 
    if (selectedDate) loadSlots(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]); // loadSlots is stable

  /** Re-render periodically on "Select Time" so slot disabled state tracks the clock (and stale selection clears). */
  useEffect(() => {
    if (activeStep !== 1 || !selectedDate) return;
    const id = window.setInterval(() => setTimeStepClockTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, [activeStep, selectedDate]);

  const loadSlots = async () => {
    const loadingDay = format(selectedDate!, 'yyyy-MM-dd');
    setLoading(true); setError(null); setSlotsLoadedFor(null);
    try {
      const resp = await scheduleService.getAvailableSlots(
        bookingData.stylistId,
        selectedDate!.toISOString(),
        bookingData.serviceId,
        bookingData.service.duration
      );
      const raw = Array.isArray(resp.data) ? resp.data : Array.isArray(resp) ? resp : [];
      const slots: TimeSlot[] = normalizeSlotsFromApi(raw as TimeSlot[]);
      setAvailableSlots(slots);
    } catch {
      setError('Unable to load slots');
      setAvailableSlots([]);
    }
    setSlotsLoadedFor(loadingDay);
    setLoading(false);
  };

  const handleDateSelect = (date: Date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    setSelectedDate(d);
    setSelectedTimeSlot(null);
  };
  const prevMonth = () => setCurrentMonth(m => subMonths(m, 1));
  const nextMonth = () => setCurrentMonth(m => addMonths(m, 1));

  /** If slots refresh, the day changes, or time passes, drop a selection that is no longer bookable. */
  useEffect(() => {
    setSelectedTimeSlot(prev => (prev && !isTimeSlotSelectable(prev, selectedDate) ? null : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isTimeSlotSelectable is imported; tick forces periodic re-check
  }, [availableSlots, selectedDate, timeStepClockTick]);

  const handleTimeSlotSelect = (slot: TimeSlot) => {
    if (!isTimeSlotSelectable(slot, selectedDate)) return;
    setRestoreNotice(null);
    setSelectedTimeSlot(slot);
  };

  const handleRedeem = async () => {
    if (appliedPromo) {
      setError('You have already applied a promotion. Remove it first to redeem loyalty points.');
      return;
    }
    if (loyaltyRedemptionId) {
      setError('Loyalty points already redeemed for this booking. Continue to checkout or cancel the redemption.');
      return;
    }
    if (pointsToRedeem < 1 || pointsToRedeem > stylistPoints) { setError('Invalid points'); return; }
    setLoading(true);
    try {
      if (!redeemIdempotencyKeyRef.current) {
        redeemIdempotencyKeyRef.current =
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? `lr_${crypto.randomUUID()}`
            : `lr_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
      }
      const res: RedeemResponse = await loyaltyService.redeemPoints(
        pointsToRedeem,
        bookingData.service.price,
        bookingData.stylistId,
        { idempotencyKey: redeemIdempotencyKeyRef.current }
      );
      setLoyaltyDiscount(res.discount);
      setLoyaltyRedemptionId(res.redemptionId);
      setLoyaltyData((prev) => {
        if (!prev) return prev;
        const nextBreakdown = (prev.breakdown || []).map((b) => {
          if (b.stylist?._id !== bookingData.stylistId) return b;
          return {
            ...b,
            availablePoints: Math.max(0, (b.availablePoints || 0) - pointsToRedeem),
          };
        });
        return {
          ...prev,
          loyaltyPoints:
            typeof res.loyaltyPoints === 'number' ? res.loyaltyPoints : prev.loyaltyPoints,
          breakdown: nextBreakdown,
        };
      });
      setPointsToRedeem(0);
      setLoyaltyMsg(`Redeemed ${pointsToRedeem} pts for ${formatCurrency(res.discount)} discount!`);
      // Keep redeemIdempotencyKeyRef until booking succeeds or release
    } catch {
      setError('Redeem failed');
    }
    setLoading(false);
  };

  const handleAddOnsSelected = (addOns: SelectedAddOn[]) => {
    console.log('[BookingPage] Add-ons selected:', addOns);
    setSelectedAddOns(addOns);
  };

  const createAppointmentIntent = async (opts?: { setupIntentId?: string }): Promise<string | null> => {
    setLoading(true); 
    setError(null);
    
    try {
      if (!selectedTimeSlot) { 
        setError('Please select a time slot'); 
        setLoading(false); 
        return null; 
      }
      if (!isTimeSlotSelectable(selectedTimeSlot, selectedDate)) {
        setError('This time slot is not available. Please choose another time.');
        setLoading(false);
        return null;
      }
      
      console.log('Creating appointment with selected time slot:', selectedTimeSlot);
      
      // Format dateTime correctly for the API
      const st = selectedTimeSlot.startTime;
      const dt = st.includes('T') ? new Date(st) : new Date(`${formatLocalDateKey(selectedDate!)}T${st}`);
      console.log('Formatted appointment date/time:', dt.toISOString());
      
      // Get affiliate referral code from sessionStorage if available
      const affiliateReferralCode = sessionStorage.getItem('affiliateReferralCode');
      const affiliateClickId = sessionStorage.getItem('affiliateClickId');
      const affiliateSessionId = sessionStorage.getItem('affiliateSessionId');
      
      // Derive discountSource
      const discountSource: DiscountSource =
        appliedPromo ? 'promotion' : loyaltyDiscount > 0 ? 'loyalty' : 'none';

      // Build promotionApplied snapshot if a promo was applied
      const promotionAppliedData: PromotionApplied | undefined = appliedPromo
        ? {
            promotionId: appliedPromo._id,
            title: appliedPromo.title,
            discountType: appliedPromo.discountType,
            discountValue: appliedPromo.discountValue,
            discountAmount: promoDiscount,
          }
        : undefined;

      // Build client note
      let clientNote = 'Booked via web app';
      if (appliedPromo) {
        clientNote = `Booked via web app with promo "${appliedPromo.title}" (-${formatCurrency(promoDiscount)})`;
      } else if (loyaltyDiscount > 0) {
        clientNote = `Booked via web app with ${pointsToRedeem} loyalty points redeemed (-${formatCurrency(loyaltyDiscount)})`;
      }

      // Create appointment data - matching mobile format
      const appointmentData: any = {
        stylist: bookingData.stylistId,
        serviceId: bookingData.serviceId,
        dateTime: dt,
        isMobileService: bookingData.isMobileService || false,
        location: bookingData.isMobileService && bookingData.location ? bookingData.location : undefined,
        notes: { client: clientNote },
        finalPrice,
        addOns: selectedAddOns.length > 0 ? selectedAddOns : undefined,
        discountSource,
        ...(promotionAppliedData && { promotionApplied: promotionAppliedData }),
        ...(loyaltyRedemptionId && discountSource === 'loyalty'
          ? { loyaltyRedemptionId }
          : {}),
      };
      
      // Add affiliate tracking data if available
      if (affiliateReferralCode) {
        appointmentData.affiliateReferralCode = affiliateReferralCode;
        if (affiliateClickId) {
          appointmentData.affiliateClickId = affiliateClickId;
        }
        if (affiliateSessionId) {
          appointmentData.affiliateSessionId = affiliateSessionId;
        }
      }

      // Card-first flow: the setupIntentId was already confirmed by Stripe before this call.
      // The backend will verify it, store the paymentMethodId, and notify the stylist atomically.
      if (opts?.setupIntentId) {
        appointmentData.setupIntentId = opts.setupIntentId;
      }
      
      // Create the appointment
      const appointment = await appointmentService.createAppointment(appointmentData);
      
      if (!appointment || !appointment._id) {
        throw new Error('Failed to create appointment - no ID returned');
      }
      
      setCreatedAppointmentId(appointment._id);
      
      setLoading(false);
      return appointment._id;
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      setLoading(false);
      if (loyaltyRedemptionId) {
        try {
          await loyaltyService.releaseLoyaltyRedemption(
            loyaltyRedemptionId,
            'Booking failed after loyalty redemption',
            redeemIdempotencyKeyRef.current
              ? { idempotencyKey: redeemIdempotencyKeyRef.current }
              : undefined
          );
          setLoyaltyDiscount(0);
          setLoyaltyRedemptionId(null);
          redeemIdempotencyKeyRef.current = null;
        } catch (releaseErr) {
          console.error('Failed to release loyalty redemption:', releaseErr);
        }
      }
      // Re-throw so callers (handleInitiateBooking) can surface the real message
      throw error;
    }
  };

  const handleGuestContinue = () => {
    if (!selectedDate || !selectedTimeSlot) return;
    savePendingBooking({
      bookingData: {
        stylistId: bookingData.stylistId,
        serviceId: bookingData.serviceId,
        service: bookingData.service,
        stylist: bookingData.stylist,
      },
      selectedDate: format(selectedDate, 'yyyy-MM-dd'),
      slotStartTime: selectedTimeSlot.startTime,
    });
    requireSignIn('booking', '/client/booking');
  };

  /**
   * Called by PaymentForm when no clientSecret is available yet.
   * Card-first flow: obtains a SetupIntent from Stripe WITHOUT creating an appointment.
   * The appointment is only created in onPaymentSuccess, after the card is confirmed.
   */
  const handleInitiateBooking = async () => {
    if (isGuest) {
      // Unreachable from the UI (guests never see Review & Book) — defense in depth.
      throw new Error('Please sign in to continue booking.');
    }
    setPaymentProcessing(true);
    setError(null);

    try {
      if (!bookingData.stylistId) {
        throw new Error('Please select a stylist before confirming.');
      }

      // Reuse an existing SetupIntent secret on retry (avoids creating duplicate intents).
      if (setupIntentData?.clientSecret && setupIntentData.setupIntentId) {
        setPaymentProcessing(false);
        return {
          clientSecret: setupIntentData.clientSecret,
          setupIntentId: setupIntentData.setupIntentId,
          appointmentId: '',
        };
      }

      const result = await paymentService.createPreBookingSetupIntent(bookingData.stylistId);
      if (result.downPaymentPolicy) {
        setDownPaymentPolicy(result.downPaymentPolicy);
      }
      setSetupIntentData({ clientSecret: result.clientSecret, setupIntentId: result.setupIntentId, appointmentId: '' });
      setPaymentProcessing(false);
      return { clientSecret: result.clientSecret, setupIntentId: result.setupIntentId, appointmentId: '' };
    } catch (err: any) {
      console.error('Failed to initiate booking:', err);
      setPaymentProcessing(false);
      throw err;
    }
  };

  const onPaymentSuccess = async (paymentMethodId?: string, setupIntentId?: string) => {
    // Card is confirmed by Stripe. Now create the appointment with the confirmed setupIntentId.
    // If appointment creation fails, the user sees a clear error — no ghost appointment exists.
    const confirmedSetupIntentId = setupIntentId || setupIntentData?.setupIntentId;
    if (!confirmedSetupIntentId) {
      setError('Payment setup incomplete. Please try again or contact support.');
      setPaymentProcessing(false);
      return;
    }

    setPaymentProcessing(true);
    try {
      const id = await createAppointmentIntent({ setupIntentId: confirmedSetupIntentId });
      if (!id) {
        throw new Error('Please select a time slot before confirming.');
      }
      navigateToSuccess(id);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Your card was saved but the booking could not be completed. Please try again.';
      setError(msg);
      setPaymentProcessing(false);
      throw err; // Re-throw so PaymentForm knows booking failed and won't show a success message
    }
  };
  
  // We've already defined BookingSuccessState at the top of the file

  // Helper function to navigate to success page
  const navigateToSuccess = (id: string | null | undefined) => {
    if (!id) return;
    
    // Calculate the down payment amount based on policy
    const totalPrice = finalPrice; // Use final price after discounts
    const depositPercent = resolvedBookingDownPaymentPercent(downPaymentPolicy);
    const downPaymentAmount = totalPrice * (depositPercent / 100);
    const remainingAmount = totalPrice;
    const remainingPercent = 100 - depositPercent;
    
    const successData: BookingSuccessState = {
      appointmentId: id,
      serviceDetails: bookingData.service,
      stylistDetails: bookingData.stylist,
      appointmentDateTime: selectedTimeSlot?.startTime || new Date().toISOString(),
      isDownPayment: downPaymentPolicy?.enabled || false,
      downPaymentAmount,
      downPaymentPercentage: depositPercent,
      remainingAmount,
      remainingPercentage: remainingPercent,
      totalAmount: totalPrice
    };
    
    navigate('/client/booking-success', { state: successData });
  };

  const isStepValid = () =>
    activeStep === 0
      ? !!selectedDate
      : !!(selectedTimeSlot && isTimeSlotSelectable(selectedTimeSlot, selectedDate));

  const generateCalendar = () => {
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDate = new Date(monthStart); startDate.setDate(startDate.getDate() - startDate.getDay());
    const endDate = new Date(monthEnd); if (endDate.getDay() !== 6) endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));
    const today = new Date(); const daysOfWeek = ['SUN','MON','TUE','WED','THU','FRI','SAT'];
    const header = <div className="calendar-header" key="header">{daysOfWeek.map(d => <div key={d} className="calendar-header__day">{d}</div>)}</div>;
    const weeks: React.ReactNode[] = [];
    let day = new Date(startDate);
    while (day <= endDate) {
      const week: React.ReactNode[] = [];
      for (let i = 0; i < 7; i++) {
        const cur = new Date(day);
        const isCurr = cur.getMonth() === currentMonth.getMonth();
        const isSel = selectedDate && cur.toDateString() === selectedDate.toDateString();
        const isTod = cur.toDateString() === today.toDateString();
        // Only exclude Sunday (0), allow Saturday (6) as it's a working day
        const isAvail = isCurr && cur.getDay() !== 0 && cur >= today;
        week.push(<CalendarDay key={cur.toString()} day={cur.getDate()} isCurrentMonth={isCurr} isSelected={!!isSel} isToday={isTod} isAvailable={isAvail} onClick={() => handleDateSelect(cur)}/>);
        day.setDate(day.getDate() + 1);
      }
      weeks.push(<div className="calendar-week" key={day.toString()}>{week}</div>);
    }
    return [header, ...weeks];
  };

  const renderTimeSlots = () => {
    if (loading) return <div className="booking-flow__spinner">Loading...</div>;
    if (error) return <div className="booking-flow__alert booking-flow__alert--error"><AlertIcon size={24}/> {error}</div>;
    if (!availableSlots.length) return <p>No available time slots</p>;
    
    // Helper to get display time - use backend's pre-formatted time if available
    const getDisplayTime = (slot: TimeSlot): string => {
      if (slot.displayTime12) {
        return slot.displayTime12; // Already formatted in client's timezone
      }
      // Fallback: parse and format
      const parse = (t: string) => (t.includes('T') ? new Date(t) : new Date(`2000-01-01T${t}`));
      return format(parse(slot.startTime), 'h:mm a');
    };
    
    // Group slots by time of day using displayTime12 or parsed time
    const parse = (t: string) => (t.includes('T') ? new Date(t) : new Date(`2000-01-01T${t}`));
    const getHour = (slot: TimeSlot): number => {
      if (slot.displayTime) {
        // Extract hour from "09:00" format
        const hour = parseInt(slot.displayTime.split(':')[0]);
        return hour;
      }
      return parse(slot.startTime).getHours();
    };
    const group = (from: number, to: number) => availableSlots.filter(s => { 
      const h = getHour(s);
      return h >= from && h < to; 
    });
    
    const renderGroup = (slots: TimeSlot[], title: string) => slots.length ? (
      <div className="booking-flow__time-group" key={title}>
        <h3>{title}</h3>
        <div className="booking-flow__time-group-slots">
          {slots.map((s) => {
            const { disabled, title: slotTitle } = getTimeSlotDisabledState(s, selectedDate);
            const selected = selectedTimeSlot?.startTime === s.startTime && !disabled;
            return (
              <button
                key={s.startTime}
                type="button"
                disabled={disabled}
                title={disabled ? slotTitle : undefined}
                aria-label={disabled ? slotTitle : undefined}
                className={`booking-flow__time-slot ${selected ? 'booking-flow__time-slot--selected' : ''} ${disabled ? 'booking-flow__time-slot--disabled' : ''}`}
                onClick={() => handleTimeSlotSelect(s)}
              >
                {getDisplayTime(s)}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;
    return (
      <div className="booking-flow__time-slots">
        {renderGroup(group(6, 12), 'Morning')}
        {renderGroup(group(12, 17), 'Afternoon')}
        {renderGroup(group(17, 22), 'Evening')}
      </div>
    );
  };

  const getStepContent = () => {
    if (activeStep === 0) return (
      <div className="booking-flow__calendar">
        <div className="booking-flow__calendar-header">
          <button onClick={prevMonth} className="booking-flow__calendar-nav"><ArrowBackIcon size={20}/></button>
          <h2>{format(currentMonth, 'MMMM yyyy')}</h2>
          <button onClick={nextMonth} className="booking-flow__calendar-nav"><ArrowForwardIcon size={20}/></button>
        </div>
        <div className="booking-flow__calendar-grid">{generateCalendar()}</div>
        {selectedDate && (
          <div className="booking-flow__selected-date">
            <CalendarIcon size={24}/> {format(selectedDate, 'EEEE, MMM d, yyyy')}
          </div>
        )}
      </div>
    );
    if (activeStep === 1) return (
      <div className="booking-flow__time-selection">
        {selectedDate ? (
          <>
            <div className="booking-flow__selected-date">
              <CalendarIcon size={24}/> {format(selectedDate, 'EEEE, MMM d, yyyy')}
            </div>
            <h2>Select a Time</h2>
            {renderTimeSlots()}
          </>
        ) : (
          <div className="booking-flow__alert booking-flow__alert--warning">Please select a date first.</div>
        )}
      </div>
    );
    // Review & Book
    return (
      <div className="booking-flow__review">
        <ServiceSummary
          service={bookingData.service}
          stylist={bookingData.stylist}
          date={selectedDate!}
          time={{ startTime: selectedTimeSlot!.startTime }}
          loyaltyDiscount={loyaltyDiscount}
          promoDiscount={promoDiscount}
          promoTitle={appliedPromo?.title}
          finalPrice={finalPrice}
          addOnsTotal={selectedAddOns.filter(a => a.sourceType === 'predefined').reduce((s, a) => s + a.priceCents, 0) / 100}
          showDownPayment={true}
          downPaymentPercentage={resolvedBookingDownPaymentPercent(downPaymentPolicy)}
        />

        {/* Add-ons Section */}
        {selectedDate && selectedTimeSlot && (
          <div className="booking-flow__addons-section">
            <div className="booking-flow__addons-header">
              <h3>Add-ons (Optional)</h3>
              <button 
                className="booking-flow__addons-button"
                onClick={() => setShowAddOnsModal(true)}
              >
                {selectedAddOns.length > 0 ? '✏️ Edit Add-ons' : '➕ Add Extras'}
              </button>
            </div>
            {selectedAddOns.length > 0 && (
              <div className="booking-flow__selected-addons">
                {selectedAddOns.map((addon, index) => (
                  <div key={index} className="booking-flow__addon-item">
                    <div className="booking-flow__addon-info">
                      <span className="booking-flow__addon-name">{addon.name}</span>
                    </div>
                    <span className="booking-flow__addon-price">
                      {formatCurrency(addon.priceCents / 100)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Promotion Selector */}
        {availablePromos.length > 0 && (
          <div className="booking-flow__promos">
            <h3 className="booking-flow__promos-title">🎁 Available Promotions</h3>
            {loyaltyDiscount > 0 && (
              <div className="booking-flow__mutual-exclusivity-notice">
                ⚠️ You have a loyalty discount applied. Remove it to apply a promotion.
              </div>
            )}
            <div className="booking-flow__promo-list">
              {availablePromos.map(promo => {
                const isApplied = appliedPromo?._id === promo._id;
                const previewDiscount = calculatePromoDiscount(promo, bookingData.service.price);
                return (
                  <div
                    key={promo._id}
                    className={`booking-flow__promo-card ${isApplied ? 'booking-flow__promo-card--active' : ''}`}
                    onClick={() => loyaltyDiscount === 0 && handleApplyPromo(promo)}
                    style={{ opacity: loyaltyDiscount > 0 ? 0.5 : 1, cursor: loyaltyDiscount > 0 ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="booking-flow__promo-card-header">
                      <span className="booking-flow__promo-card-title">{promo.title}</span>
                      <span className="booking-flow__promo-card-discount">
                        {promo.discountType === 'percentage'
                          ? `${promo.discountValue}% off`
                          : formatCurrency(promo.discountValue)}
                      </span>
                    </div>
                    {promo.description && (
                      <p className="booking-flow__promo-card-desc">{promo.description}</p>
                    )}
                    <div className="booking-flow__promo-card-footer">
                      <div>
                        <span className="booking-flow__promo-card-saving">
                          Save {formatCurrency(previewDiscount)}
                        </span>
                        {(promo.minBookingAmount ?? 0) > 0 && (
                          <span style={{ fontSize: 11, color: '#999', marginLeft: 8 }}>
                            (min. {formatCurrency(promo.minBookingAmount ?? 0)})
                          </span>
                        )}
                      </div>
                      <span className={`booking-flow__promo-card-badge ${isApplied ? 'applied' : ''}`}>
                        {isApplied ? '✓ Applied' : 'Apply'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {stylistPoints > 0 && (
          <div style={{ opacity: appliedPromo ? 0.5 : 1, pointerEvents: appliedPromo ? 'none' : 'auto' }}>
            {appliedPromo && (
              <div className="booking-flow__mutual-exclusivity-notice">
                ⚠️ You have a promotion applied. Remove it to redeem loyalty points.
              </div>
            )}
            {/* C4: Show active loyalty discount with a Remove button */}
            {loyaltyDiscount > 0 && !appliedPromo && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: '#FFFBEB', border: '1.5px solid #FCD34D',
                borderRadius: 12, padding: '10px 14px', marginBottom: 12,
              }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#92400E' }}>
                  ⭐ Loyalty discount applied: -{formatCurrency(loyaltyDiscount)}
                </span>
                <button
                  onClick={() => { setLoyaltyDiscount(0); setPointsToRedeem(0); }}
                  style={{
                    background: 'transparent', border: '1px solid #D97706',
                    color: '#D97706', borderRadius: 8, padding: '4px 12px',
                    cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}
                >
                  Remove
                </button>
              </div>
            )}
            {loyaltyMsg && (
              <div className="booking-flow__alert booking-flow__alert--success">
                <CheckIcon size={16}/> {loyaltyMsg}
              </div>
            )}
            {loyaltyDiscount === 0 && (
              <LoyaltyPointsSelector
                availablePoints={stylistPoints}
                pointsToRedeem={pointsToRedeem}
                onPointsChange={setPointsToRedeem}
                onRedeem={handleRedeem}
                isLoading={loading}
                stylistName={bookingData.stylist.name}
              />
            )}
          </div>
        )}
        
        <div className="booking-flow__payment">
          <PaymentForm
            amount={finalPrice}
            clientSecret={setupIntentData?.clientSecret ?? null}
            onPaymentSuccess={onPaymentSuccess}
            onInitiateBooking={handleInitiateBooking}
            loading={paymentProcessing}
            downPaymentPercentage={resolvedBookingDownPaymentPercent(downPaymentPolicy)}
            mode="setup"
          />
        </div>
      </div>
    );
  };  

  if (!bookingData) {
    return <div className="booking-flow__error">Missing booking information.</div>;
  }

  return (
    <div className="booking-flow-container">
      <div className="booking-flow__header">
        <button className="booking-flow__header-back" onClick={() => navigate(-1)}>
          <ArrowBackIcon size={16}/> Back
        </button>
      </div>
      <div className="booking-flow__stepper">
        {steps.map((label, idx) => (
          <Step
            key={label}
            number={idx + 1}
            label={label}
            active={activeStep === idx}
            completed={activeStep > idx}
          />
        ))}
      </div>
      {restoreNotice && (
        <div className="booking-flow__alert booking-flow__alert--error" role="status">
          <AlertIcon size={20}/> {restoreNotice}
        </div>
      )}
      {error && !loading && (
        <div className="booking-flow__alert booking-flow__alert--error">
          <AlertIcon size={20}/> {error}
        </div>
      )}
      <div className="booking-flow__content">
        {getStepContent()}
      </div>
      <div className="booking-flow__footer">
        <button
          className="booking-flow__button booking-flow__button--text"
          onClick={() => setActiveStep(s => s - 1)}
          disabled={activeStep === 0 || loading || paymentProcessing}
        >
          Back
        </button>
        {/* On the last step the PaymentForm provides its own "Confirm & Book" submit button */}
        {activeStep < steps.length - 1 && (
          isGuest && activeStep === steps.length - 2 ? (
            <button
              className="booking-flow__button booking-flow__button--primary"
              data-testid="guest-continue-booking"
              onClick={handleGuestContinue}
              disabled={!isStepValid() || loading}
            >
              Sign in to continue booking
            </button>
          ) : (
            <button
              className="booking-flow__button booking-flow__button--primary"
              onClick={() => setActiveStep(s => s + 1)}
              disabled={!isStepValid() || loading}
            >
              {loading ? 'Loading...' : 'Next'}
            </button>
          )
        )}
      </div>
      {/* Always render ChatInterface but conditionally show/hide it */}
      <div style={{ display: chatOpen ? 'block' : 'none' }}>
        {chatThreadId && <ChatInterface threadId={chatThreadId} />}
      </div>
      
      {!chatOpen && (
        <button
          className="booking-flow__chat-fab"
          aria-label="Message stylist"
          onClick={() => (isGuest ? requireSignIn('messaging') : setChatOpen(true))}
        >
          <ChatIcon size={24}/>
        </button>
      )}

      {/* Add-ons Modal */}
      {showAddOnsModal && (
        <AddOnsSelection
          serviceId={bookingData.serviceId}
          serviceName={bookingData.service.name}
          servicePrice={bookingData.service.price}
          serviceCategory={bookingData.service.category || 'hair'}
          onAddOnsSelected={handleAddOnsSelected}
          onClose={() => setShowAddOnsModal(false)}
          existingAddOns={selectedAddOns}
        />
      )}
    </div>
  );
};

export default BookingPage;
